import { TranslatedText } from "@/components/common/text";
import { InfoButton } from "@/components/common/buttons";
import { Skeleton } from "@/components/common/other/Skeleton";
import { Hr } from "@/components/common/other/Hr";
import { cn } from "@/utils/css";

const Chip = ({ className }) => (
    <div className={cn("overflow-hidden rounded", className)}>
        <Skeleton />
    </div>
);

const MarketRateGraph = () => (
    <div className="flex h-[60px] flex-row items-start justify-between gap-4">
        <div className="flex flex-col items-start">
            <div className="flex h-[19px] items-center">
                <Chip className="h-4 w-7" />
            </div>
            <div className="flex h-[25px] items-center">
                <Chip className="h-5 w-16" />
            </div>
        </div>
        <div className="flex flex-col items-end">
            <div className="flex h-[19px] items-center">
                <Chip className="h-4 w-7" />
            </div>
            <div className="flex h-[25px] items-center">
                <Chip className="h-5 w-16" />
            </div>
        </div>
    </div>
);

const MarketRateBottom = () => (
    <>
        <div className="flex flex-col gap-1">
            <TranslatedText className="uppercase text-xs/[19px] text-cm-p-text-secondary font-medium">Lane stats</TranslatedText>
            <div className="flex h-[22px] flex-row items-center gap-3">
                <Chip className="h-4 w-7" />
                <span className="size-0.5 rounded-full bg-cm-p-text-primary" />
                <Chip className="h-4 w-[109px]" />
            </div>
        </div>
        <Hr className="my-0" />
        {/* The title depends on the response, so it shimmers too. */}
        <div className="flex flex-col gap-2">
            <div className="flex h-[19px] flex-row items-center">
                <Chip className="h-4 w-[110px]" />
            </div>
            <Chip className="h-4 w-[205px]" />
        </div>
    </>
);

const BuyRateGraph = () => (
    <div className="flex flex-col">
        <div className="flex w-full flex-row gap-1">
            <div className="flex h-[135px] w-[26px] shrink-0 flex-col justify-between text-xs/[19px] text-cm-p-text-secondary">
                <span>95%</span>
                <span>50%</span>
                <span>5%</span>
            </div>
            <svg viewBox="0 0 285 135" preserveAspectRatio="none" className="h-[135px] min-w-0 grow">
                <polygon points="0,135 285,135 285,0" className="fill-cm-p-surface-container" />
            </svg>
        </div>
        <div className="flex flex-row justify-between pl-[30px] pt-1">
            {Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="flex h-[19px] items-start">
                    <Chip className="h-4 w-7" />
                </div>
            ))}
        </div>
    </div>
);

const BuyRateBottom = () => (
    <div className="flex flex-row items-center gap-2">
        <div className="flex min-w-0 flex-1 flex-row items-center">
            <TranslatedText className="uppercase text-xs/[19px] text-cm-p-text-secondary font-medium">
                Margin calculator
            </TranslatedText>
        </div>
        <div className="flex min-w-0 flex-1 flex-row gap-1">
            <Chip className="h-10 min-w-0 flex-1" />
            <Chip className="h-10 min-w-0 flex-1" />
        </div>
    </div>
);

const VARIANTS = {
    market: { Graph: MarketRateGraph, Bottom: MarketRateBottom },
    buy: { Graph: BuyRateGraph, Bottom: BuyRateBottom },
};

// Spacing mirrors the loaded cards so nothing shifts when the data arrives.
export const RateCardSkeleton = ({ variant = "market", title, infoText }) => {
    const { Graph, Bottom } = VARIANTS[variant] ?? VARIANTS.market;

    return (
        <div className="flex flex-col w-full grow gap-5">
            <div className="flex flex-col gap-7">
                <div className="flex flex-col gap-2">
                    <div className="flex flex-row items-center justify-between gap-2">
                        <div className="flex flex-row items-center gap-2">
                            <TranslatedText className="text-base text-cm-p-text-primary font-medium leading-normal">
                                {title}
                            </TranslatedText>
                            {infoText ? <InfoButton text={infoText} /> : null}
                        </div>
                        <Chip className="h-5 w-[126px] shrink-0" />
                    </div>
                    <div className="flex h-[30px] flex-row items-center gap-2">
                        <Chip className="h-6 w-[83px]" />
                        <Chip className="h-4 w-[50px]" />
                        <div className="flex flex-1 justify-end">
                            <Chip className="h-4 w-[86px]" />
                        </div>
                    </div>
                </div>
                <Graph />
            </div>

            <div className="flex flex-col grow gap-3">
                <Hr className="my-0" />
                <Bottom />
            </div>
        </div>
    );
};
